import React, { useContext } from "react";
import "./Calculator.css";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import ToggleButton from "@material-ui/lab/ToggleButton";
import Typography from "@material-ui/core/Typography";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import { useForm, Controller } from "react-hook-form";
import { withRouter } from "react-router-dom";
import CrclContext from "../contexts/CrclContext";
import { DataUsageSharp } from "@material-ui/icons";
import { fullAntiCoagulationText } from "../TextOutputs/FullAntiCoagulationText";
import { ProphylacticText } from "../TextOutputs/ProphylacticText";

const Calculator = (props) => {
  const crclcontext = useContext(CrclContext);

  function calculateIdealWeight(height, sex) {
    //calculate ideal weight = (height-152)*0.9+50
    return Number(((height - 152) * 0.9 + (sex === "male" ? 50 : 45.5)).toFixed(3));
  }

  function calculateBMI(heightM, weight) {
    return Number((weight / (heightM*heightM)));
  }

  function calculateCrCl(age, weight, creatinine, sex){
    let crcl = ((140 - age) * weight) / (0.814 * creatinine);
    return sex === "male" ? crcl : crcl * .85;
  }

  function getRenalImpairment(crcl){
    if (crcl < 30) return 'severe';
    else if (crcl >= 30 && crcl <= 50) return 'moderate';
    else if (crcl > 50) return 'minor';
  }

  function getCrClSub(crcl){
    if (crcl > 50) {
      return "";
    } else if (crcl < 50 && crcl >= 30) {
      return "Moderate renal impairment";
    } else if (crcl < 30 && crcl >= 0.1) {
      return "Severe renal impairment";
    }
  }

  function getCrClSubClass(crcl){
    if (crcl > 50) {
      return "green";
    } else if (crcl < 50 && crcl >= 30) {
      return "yellow";
    } else if (crcl < 30 && crcl >= 0.1) {
      return "red";
    }
  }

  const calculate = (e, data) => {
    //Validation text
    let validationText = {};
    let alertDialog = false;
    let alertDialogText = [];
    let alertTitle = "";

    //Start of calculations
    let idealWeight;
    let bmi;
    let crcl;
    let crclIdeal;
    let crclSub;
    let crclClass;
    let crclSubActual;
    let crclSubIdeal;
    let anticoagulationText = [];
    let prophylaxisText = [];
    let prophylaxisDose;

    idealWeight = calculateIdealWeight(data.height, data.sex);
    bmi = calculateBMI(data.height/100, data.weight);

    //Calculate CrCl
    crcl = calculateCrCl(data.age, data.weight, data.creatinine, data.sex);
    crclIdeal = calculateCrCl(data.age, idealWeight, data.creatinine, data.sex);

    //Define CrCl warning text
    const lowerCrCl = Math.min(crcl, crclIdeal);
    crclSub = getCrClSub(lowerCrCl);
    crclClass = getCrClSubClass(lowerCrCl);
    crclSubIdeal = getCrClSub(crclIdeal);
    crclSubActual = getCrClSub(crcl);

    // New structure
    // Full Anti-Coagulation
    
    // New structure with differing weight conditions.
    // Conditions for anticoagulation table:
    let renalImpairmentLevel = getRenalImpairment(lowerCrCl);
    let weightInRange = (data.weight >= 50 && data.weight <= 100);
    let weightEdgeLimit = (data.weight < 50 || (data.weight > 100 && data.weight <= 120));
    let overWeightLimit = (data.weight > 120 || (bmi > 40));

     // DG TESTING
    //  console.log('weight', data.weight);
    //  console.log('idealWeight', idealWeight);
    //  console.log('bmi', bmi);
    //  console.log('crcl', crcl);
    //  console.log('crclIdeal', crclIdeal);
    //  console.log('renalImpairmentLevel', renalImpairmentLevel);
    //  console.log('weightInRange', weightInRange);
    //  console.log('weightEdgeLimit', weightEdgeLimit);
    //  console.log('overWeightLimit', overWeightLimit);

    anticoagulationText.push([fullAntiCoagulationText(renalImpairmentLevel, weightInRange, weightEdgeLimit, overWeightLimit, data.weight)]);
    
     //Prophylaxis
    prophylaxisText.push([ProphylacticText(renalImpairmentLevel, overWeightLimit, data.weight)]);

    // Always show warning dialog.
    alertDialog = true;

    alertTitle = "Warning";
    if (data.weight > 150 || data.height > 200) {
      alertDialogText.push(
        <Typography variant="body1" paragraph id="alert-dialog-warning">
          <strong>
          You have entered a weight larger than 150kg/height larger than 200cm. 
          
          Please check measurements carefully and note that estimates of renal function may be inaccurate at extremes of body size.
          </strong>
        </Typography>,
      );
    }
    alertDialogText.push(
      <Typography variant="body1" paragraph id="alert-dialog-warning">
        <strong>
          Prior to prescribing or administering an anticoagulant, please
          ensure duplication of therapeutic anticoagulation does not occur.

          If the patient is already on an anticoagulant, 
          check for appropriate changeover and administration times.
        </strong>
      </Typography>,
    );

    validationText = {
      warningText: alertDialogText,
    };

    crclcontext.update({
      validationText: validationText,
      alertDialog: alertDialog,
      alertTitle: alertTitle,
      crcl: crcl.toFixed(1),
      crclIdeal: crclIdeal.toFixed(1),
      crclSub: crclSub,
      crclClass: crclClass,
      crclSubActual: crclSubActual,
      crclSubIdeal: crclSubIdeal,
      idealWeight: idealWeight,
      anticoagulationText: anticoagulationText,
      prophylaxisText: prophylaxisText,
      renalImpairmentLevel: renalImpairmentLevel
    });

    e.preventDefault();
    props.history.push("/results");
  };

  const defaultValues = {
    age: '',
    weight: '',
    height: '',
    creatinine: '',
  };

  const { handleSubmit, register, errors, setValue, reset, control } = useForm({
    mode: "onChange",
    defaultValues: defaultValues,
  });

  const onSubmit = (data, e) => {
    if (!data.sex || data.sex === null) {
      let validationText = { sexHelperText: "Select a sex" };
      crclcontext.update({ validationText: validationText, ...data });
      return;
    }
    crclcontext.update({ ...data });
    calculate(e, data);
  };

  const [sex, setSex] = React.useState(() => crclcontext.sex);
  const handleSex = (e, newSex) => {
    setSex(newSex);
    setValue("sex", newSex);
  };

  React.useEffect(() => {
    register("sex");
  }, [register]);

  React.useEffect(() => {
    const formData = { ...crclcontext };
    setValue("sex", formData.sex);
    setValue("age", formData.age);
    setValue("weight", formData.weight);
    setValue("height", formData.height);
    setValue("creatinine", formData.creatinine);
  }, [setValue, crclcontext]);

  const resetHandler = () => {
    handleSex({}, null);
    reset();
  };

  return (
    <div className="calculator">
      <Typography variant="body1" gutterBottom>
        All fields are required.
      </Typography>
      <Typography fontSize="14" variant="body2" gutterBottom>
        Sex *
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ToggleButtonGroup
              name="sex"
              value={sex}
              size="large"
              exclusive
              required
              className="toggle"
              onChange={handleSex}
              aria-label="select sex"
            >
              <ToggleButton value="male" aria-label="Male" className="toggle">
                Male
              </ToggleButton>
              <ToggleButton
                value="female"
                aria-label="Female"
                className="toggle"
              >
                Female
              </ToggleButton>
            </ToggleButtonGroup>
            <FormHelperText error id="my-helper-text">
              {crclcontext.validationText.sexHelperText}
            </FormHelperText>
          </Grid>
          <Grid item xs={12}>
            <Controller
              as={<TextField aria-label="age" />}
              control={control}
              name="age"
              label="Age" 
              type="number" 
              variant="filled"
              fullWidth
              required
              rules={{
                min: { value: 18, message: "Enter an age above 18" },
                max: { value: 100, message: "Enter an age below 100" },
              }}
              error={errors.age ? true : false}
              helperText={errors.age?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              as={
                <TextField aria-label="weight" />
              }
              control={control}
              name="weight"
              label="Weight (kg)" 
              type="number"
              variant="filled"
              fullWidth
              required
              rules={{
                min: { value: 20, message: "Weight is too low" },
                max: { value: 300, message: "Weight is too high" },
              }}
              error={errors.weight ? true : false}
              helperText={errors.weight?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              as={
                <TextField aria-label="height" />
              }
              control={control}
              name="height"
              label="Height (cm)" 
              type="number"
              variant="filled"
              fullWidth
              required
              rules={{
                min: { value: 152, message: "Height is too low" },
                max: { value: 250, message: "Height is too high" },
              }}
              error={errors.height ? true : false}
              helperText={errors.height?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              as={
                <TextField aria-label="creatinine" />
              }
              control={control}
              name="creatinine"
              label="Serum creatinine (umol/L)" 
              type="number"
              variant="filled"
              fullWidth
              required
              rules={{}}
              error={errors.creatinine ? true : false} 
              helperText={errors.creatinine?.message} 
            />
          </Grid>
          <Grid container justify="space-between" className="buttonClass">
            <Button
              variant="outlined"
              color="primary"
              type="reset"
              onClick={resetHandler}
            >
              Reset
            </Button>
            <Button variant="contained" color="primary" type="submit">
              Calculate CrCl
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default withRouter(Calculator);
